<template>
  <div id="teste-webpki">
    <Header :logged="logged" />
    
    <div class="row justify-content-center mt-5">
      <div class="col-md-8">
        <div class="card shadow">
          <div class="card-header bg-primary text-white">
            <h4 class="mb-0">
              <i class="fas fa-certificate mr-2"></i>
              Teste Web PKI - Lacuna Software
            </h4>
          </div>
          
          <div class="card-body">
            <!-- Status do Plugin -->
            <div class="alert" :class="pluginStatusClass" role="alert">
              <strong>Status do Plugin:</strong> {{ pluginStatus }}
            </div>

            <!-- Informações do Sistema PKI -->
            <div class="alert alert-secondary mb-4">
              <h6><i class="fas fa-server mr-2"></i>Configuração PKI:</h6>
              <p class="mb-1"><strong>Backend PKI:</strong> {{ pkiBackendUrl }}</p>
              <p class="mb-1"><strong>Rest PKI Core:</strong> {{ restPKIUrl }}</p>
              <p class="mb-0"><small class="text-muted">Escolha qual servidor usar para validação do certificado digital</small></p>
            </div>

            <!-- Seleção do tipo de validação -->
            <div class="alert alert-info mb-4">
              <h6><i class="fas fa-cog mr-2"></i>Tipo de Validação:</h6>
              <div class="form-check form-check-inline">
                <input class="form-check-input" type="radio" name="tipoValidacao" id="backendAtual" v-model="tipoValidacao" value="backend">
                <label class="form-check-label" for="backendAtual">
                  Backend PKI Atual (pki.cartoriosdeprotesto.org.br)
                </label>
              </div>
              <div class="form-check form-check-inline">
                <input class="form-check-input" type="radio" name="tipoValidacao" id="restPKI" v-model="tipoValidacao" value="restpki">
                <label class="form-check-label" for="restPKI">
                  Rest PKI Core (Local - Porta 9796)
                </label>
              </div>
            </div>

            <!-- Área de informações do certificado -->
            <div v-if="certificadoSelecionado" class="alert alert-info">
              <h6><i class="fas fa-user mr-2"></i>Certificado Selecionado:</h6>
              <p class="mb-1"><strong>Nome:</strong> {{ certificadoSelecionado.subjectName }}</p>
              <p class="mb-1"><strong>Emissor:</strong> {{ certificadoSelecionado.issuerName }}</p>
              <p class="mb-0"><strong>Validade:</strong> {{ formatarData(certificadoSelecionado.validityEnd) }}</p>
            </div>

            <!-- Botões de ação -->
            <div class="row mb-4">
              <div class="col-md-6">
                <button 
                  class="btn btn-outline-primary btn-block"
                  @click="verificarPlugin"
                  :disabled="loading">
                  <i class="fas fa-search mr-2"></i>
                  {{ loading && currentAction === 'verificar' ? 'Verificando...' : 'Verificar Plugin' }}
                </button>
              </div>
              
              <div class="col-md-6">
                <button 
                  class="btn btn-primary"
                  @click="listarCertificados"
                  :disabled="loading || !pluginDetectado">
                  <i class="fas fa-list mr-2"></i>
                  {{ loading && currentAction === 'listar' ? 'Carregando...' : 'Listar Certificados' }}
                </button>
                
                <button 
                  class="btn btn-warning ml-2"
                  @click="listarTodosCertificados"
                  :disabled="loading || !pluginDetectado">
                  <i class="fas fa-bug mr-2"></i>
                  Debug: Listar Todos
                </button>
              </div>
            </div>

            <!-- Lista de certificados -->
            <div v-if="certificados.length > 0" class="mb-4">
              <h6><i class="fas fa-certificate mr-2"></i>Certificados Disponíveis:</h6>
              <div class="list-group">
                <button 
                  v-for="(cert, index) in certificados" 
                  :key="index"
                  class="list-group-item list-group-item-action"
                  :class="{ 'active': certificadoSelecionado && certificadoSelecionado.thumbprint === cert.thumbprint }"
                  @click="selecionarCertificado(cert)">
                  <div class="d-flex w-100 justify-content-between">
                    <h6 class="mb-1">{{ extrairNome(cert.subjectName) }}</h6>
                    <small>{{ formatarData(cert.validityEnd) }}</small>
                  </div>
                  <p class="mb-1">{{ cert.issuerName }}</p>
                  <small>Thumbprint: {{ cert.thumbprint.substring(0, 20) }}...</small>
                </button>
              </div>
            </div>

            <!-- Área de teste de login -->
            <div v-if="certificadoSelecionado" class="border rounded p-3 mb-3">
              <h6><i class="fas fa-sign-in-alt mr-2"></i>Teste de Login com Certificado:</h6>
              
              <div class="alert alert-info">
                <small>
                  <strong>Certificado selecionado:</strong> {{ extrairNome(certificadoSelecionado.subjectName) }}<br>
                  <strong>CPF/CNPJ:</strong> {{ extrairDocumento(certificadoSelecionado.subjectName) }}<br>
                  <strong>Validade:</strong> {{ formatarData(certificadoSelecionado.validityEnd) }}
                </small>
              </div>

              <button 
                class="btn btn-primary mr-2"
                @click="testarLoginCertificado"
                :disabled="loading">
                <i class="fas fa-key mr-2"></i>
                {{ loading && currentAction === 'login' ? 'Autenticando...' : 'Teste Login Certificado' }}
              </button>

              <button 
                class="btn btn-info mr-2"
                @click="testarLoginComRestPKI"
                :disabled="loading">
                <i class="fas fa-shield-alt mr-2"></i>
                {{ loading && currentAction === 'login' ? 'Validando...' : 'Login Rest PKI Core' }}
              </button>

              <button 
                class="btn btn-success mr-2"
                @click="fazerLoginRestPKI"
                :disabled="loading">
                <i class="fas fa-key mr-2"></i>
                {{ loading && currentAction === 'login-restpki' ? 'Autenticando...' : 'Login Rest PKI Session' }}
              </button>
              
              <button 
                class="btn btn-warning"
                @click="assinarTexto"
                :disabled="loading || !textoParaAssinar.trim()">
                <i class="fas fa-signature mr-2"></i>
                {{ loading && currentAction === 'assinar' ? 'Assinando...' : 'Assinar Texto' }}
              </button>
            </div>

            <!-- Resultado do login -->
            <div v-if="loginResult" class="border rounded p-3 mb-3">
              <h6><i class="fas fa-check-circle mr-2 text-success"></i>Resultado do Login:</h6>
              <div class="alert" :class="loginResult.success ? 'alert-success' : 'alert-danger'">
                <strong>{{ loginResult.success ? 'Login realizado com sucesso!' : 'Erro no login' }}</strong>
                <p class="mb-0 mt-2">{{ loginResult.message }}</p>
                <div v-if="loginResult.userInfo" class="mt-2">
                  <small>
                    <strong>Usuário:</strong> {{ loginResult.userInfo.name }}<br>
                    <strong>Email:</strong> {{ loginResult.userInfo.email }}<br>
                    <strong>CPF:</strong> {{ loginResult.userInfo.cpf }}
                  </small>
                </div>
              </div>
            </div>

            <!-- Área de teste de assinatura -->
            <div v-if="certificadoSelecionado" class="border rounded p-3 mb-3">
              <h6><i class="fas fa-edit mr-2"></i>Teste de Assinatura:</h6>
              
              <div class="form-group">
                <label for="textoAssinar">Texto para assinar:</label>
                <textarea 
                  id="textoAssinar"
                  v-model="textoParaAssinar" 
                  class="form-control" 
                  rows="3"
                  placeholder="Digite o texto que deseja assinar...">
                </textarea>
              </div>

              <button 
                class="btn btn-success"
                @click="assinarTexto"
                :disabled="loading || !textoParaAssinar.trim()">
                <i class="fas fa-signature mr-2"></i>
                {{ loading && currentAction === 'assinar' ? 'Assinando...' : 'Assinar Texto' }}
              </button>
            </div>

            <!-- Resultado da assinatura -->
            <div v-if="assinatura" class="border rounded p-3">
              <h6><i class="fas fa-check-circle mr-2 text-success"></i>Assinatura Gerada:</h6>
              <div class="form-group">
                <label>Assinatura Base64:</label>
                <textarea 
                  class="form-control" 
                  rows="4" 
                  :value="assinatura" 
                  readonly>
                </textarea>
              </div>
              
              <button 
                class="btn btn-outline-secondary btn-sm"
                @click="copiarAssinatura">
                <i class="fas fa-copy mr-2"></i>
                Copiar Assinatura
              </button>
            </div>

            <!-- Log de ações -->
            <div v-if="logs.length > 0" class="mt-4">
              <h6><i class="fas fa-history mr-2"></i>Log de Ações:</h6>
              <div class="border rounded p-2" style="max-height: 200px; overflow-y: auto;">
                <small v-for="(log, index) in logs" :key="index" class="d-block">
                  <span class="text-muted">[{{ log.timestamp }}]</span> {{ log.message }}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Header from "../components/Header";
import API from "../services/api";
import APIConsulta from "../services/api_consulta";
import CryptoJSAesJson from "../services/CryptoJSAesJson";
import RestPKICore from "../services/rest-pki-core";

export default {
  name: "TesteWebPKI",
  components: {
    Header
  },
  
  data() {
    return {
      logged: false,
      loading: false,
      currentAction: '',
      pluginDetectado: false,
      pluginStatus: 'Não verificado',
      certificados: [],
      certificadoSelecionado: null,
      textoParaAssinar: 'Este é um texto de teste para assinatura digital com CENPROT.',
      assinatura: '',
      logs: [],
      pki: null, // Instância do Web PKI
      loginResult: null,
      certificadoInfo: null,
      tipoValidacao: 'backend', // 'backend' ou 'restpki'
      restPKIInfo: null
    }
  },

  computed: {
    pluginStatusClass() {
      if (!this.pluginDetectado) {
        return 'alert-warning';
      }
      return 'alert-success';
    },

    pkiBackendUrl() {
      return process.env.VUE_APP_REST_PKI_URL || 'URL não configurada';
    },

    restPKIUrl() {
      return process.env.VUE_APP_REST_PKI_URL || 'https://pki.cenprotnacional.org.br/';
    }
  },

  mounted() {
    this.adicionarLog('Página carregada');
    this.verificarLogin();
    this.carregarWebPKI();
    this.testarRestPKI(); // Comentado para evitar erro de CORS
  },

  methods: {
    async verificarLogin() {
      try {
        await this.$root.isLogged();
        this.logged = true;
      } catch (error) {
        this.logged = false;
      }
    },

    carregarWebPKI() {
      this.adicionarLog('Iniciando carregamento do Web PKI...');
      
      // Criar script tag para carregar o Web PKI
      const script = document.createElement('script');
      script.src = 'https://cdn.lacunasoftware.com/libs/web-pki/lacuna-web-pki-2.14.0.min.js';
      script.onload = () => {
        this.adicionarLog('Web PKI carregado com sucesso');
        this.inicializarWebPKI();
      };
      script.onerror = () => {
        this.adicionarLog('Erro ao carregar o Web PKI');
        this.pluginStatus = 'Erro ao carregar biblioteca';
      };
      
      document.head.appendChild(script);
    },

    async testarRestPKI() {
      this.adicionarLog('Testando conexão com Rest PKI Core...');
      try {
        const info = await RestPKICore.obterInfoServidor();
        this.restPKIInfo = info;
        this.adicionarLog(`Rest PKI Core conectado - ${info.productName} v${info.version}`);
      } catch (error) {
        this.adicionarLog(`Erro ao conectar Rest PKI Core: ${error.message}`);
      }
    },

    inicializarWebPKI() {
      var licenca_lacuna = 'AusAYXBpLmNlbnByb3RuYWNpb25hbC5vcmcuYnIsYXBpLnBlc3F1aXNhcHJvdGVzdG8uY29tLmJyLGNhcnRvcmlvLmNlbnByb3RuYWNpb25hbC5vcmcuYnIsY2VucHJvdG5hY2lvbmFsLm9yZy5icixwa2kuY2FydG9yaW9zZGVwcm90ZXN0by5vcmcuYnIscGtpaG9tb2xvZ2EuY2FydG9yaW9zZGVwcm90ZXN0by5vcmcuYnIsc2l0ZS5jZW5wcm90bmFjaW9uYWwub3JnLmJyLHd3dy5wZXNxdWlzYXByb3Rlc3RvLmNvbS5icsoAaG1sLmFwaS5jZW5wcm90bmFjaW9uYWwuY29tLmJyLGlwNDoxMC4wLjAuMC84LGlwNDoxMC4wLjAuMC84LGlwNDoxMjcuMC4wLjAvOCxpcDQ6MTI3LjAuMC4wLzgsaXA0OjE3Mi4xNi4wLjAvMTIsaXA0OjE3Mi4xNi4wLjAvMTIsaXA0OjE5Mi4xNjguMC4wLzE2LGlwNDoxOTIuMTY4LjAuMC8xNixzaXRlLWhvbW9sb2cuY2VucHJvdG5hY2lvbmFsLm9yZy5icgMAUHJvAAAAAaovp0anSN3QSX7F/TNYIjbQsMqrqqW+74O01Do6s/PzKMvXwp1s9F8sthQztKRvws/+QL+PX3uBoMb1Rp5fKNOUemjxf6IkxUKJ+QRGFAciBZ0S3wSrueT66sOZM1Z15ylowRaPqz2BvyPIc3fmah7AaV0lpueG0X4suFZ+PGo1/tmHMzijcNqH0fndF6VgMyVYmTBhOH5l8mWWBfNonb+Ub/mMytkpBpLX/u2E2myej/emEy5YVO9fnFgQAlJLdDCB2dZDa7QS2XR0MYjPlIoiKfdfHmXhmAcv2halIVAbmkrByK8EQ3mHTKkA2vBdhCexFQtnXo7z4E9zpa8ixsg=';
      if (typeof LacunaWebPKI !== 'undefined') {
        this.pki = new LacunaWebPKI(licenca_lacuna);
        this.adicionarLog('Web PKI inicializado');
        this.verificarPlugin();
      } else {
        this.adicionarLog('LacunaWebPKI não encontrado');
        this.pluginStatus = 'Biblioteca não carregada';
      }
    },

async verificarPlugin() {
  this.loading = true;
  this.currentAction = 'verificar';
  this.adicionarLog('Verificando disponibilidade do plugin...');

  const initPromise = new Promise((resolve, reject) => {
    this.pki.init({
      ready: () => {
        this.pluginDetectado = true;
        this.pluginStatus = 'Plugin detectado e funcionando';
        this.adicionarLog('Plugin Web PKI detectado com sucesso');
        resolve();
      },
      notInstalled: () => {
        this.pluginDetectado = false;
        this.pluginStatus = 'Plugin não instalado';
        this.adicionarLog('Plugin Web PKI não está instalado');
        this.mostrarInstrucoesInstalacao();
        reject(new Error('Plugin não instalado')); // <-- FALHA: A Promise é rejeitada
      },
      outdated: () => {
        this.pluginDetectado = false;
        this.pluginStatus = 'Plugin desatualizado';
        this.adicionarLog('Plugin Web PKI está desatualizado');
        reject(new Error('Plugin desatualizado')); // <-- FALHA: A Promise é rejeitada
      }
    });
  });

  try {
    await initPromise;
  } catch (error) {
    this.adicionarLog(`Erro ao verificar plugin: ${error.message}`);
    this.pluginStatus = `Erro: ${error.message}`;
  } finally {
    this.loading = false;
    this.currentAction = '';
  }
},

async listarCertificados() {
  if (!this.pluginDetectado) {
    this.$bvToast.toast('Plugin Web PKI não está disponível', {
      title: 'Erro',
      variant: 'danger'
    });
    return;
  }

  this.loading = true;
  this.currentAction = 'listar';
  this.adicionarLog('Carregando lista de certificados...');

  try {
    const certificados = await new Promise((resolve, reject) => {
      
      this.pki.listCertificates().success((lista) => {
        resolve(lista); 
      }).fail((error) => {
        reject(error); 
      });

    });

    // 3. Neste ponto, 'certificados' DEVE ser um array (ou o 'catch' teria sido ativado)
    // A verificação de 'Array.isArray' agora é redundante, mas vamos mantê-la por segurança.
    if (!Array.isArray(certificados)) {
      this.adicionarLog(`Erro inesperado: A chamada teve sucesso, mas não retornou um array.`);
      throw new Error('Formato de resposta inesperado da biblioteca Web PKI.');
    }

    this.certificados = certificados;
    this.adicionarLog(`${certificados.length} certificado(s) encontrado(s)`);
    
    if (certificados.length === 0) {
      this.adicionarLog('Nenhum certificado digital foi encontrado no computador.');
      this.$bvToast.toast('Nenhum certificado encontrado no computador', {
        title: 'Aviso',
        variant: 'warning'
      });
    }
  } catch (error) {
    // O 'error' aqui virá do 'reject()' ou de um erro de 'fail()'
    let errorMessage = error.message || error.toString();
    this.adicionarLog(`Erro ao listar certificados: ${errorMessage}`);
    this.$bvToast.toast(`Erro ao listar certificados: ${errorMessage}`, {
      title: 'Erro',
      variant: 'danger'
    });
  } finally {
    this.loading = false;
    this.currentAction = '';
  }
},

    async listarTodosCertificados() {
      this.loading = true;
      this.currentAction = 'debug';
      this.adicionarLog('🔍 Executando debug: listando certificados de todos os locais...');

      try {
        const resultados = await WebPKIService.listarTodosCertificados();
        
        this.adicionarLog(`📋 Debug concluído! Encontrados certificados em ${resultados.length} locais:`);
        
        resultados.forEach(resultado => {
          this.adicionarLog(`📂 ${resultado.local}: ${resultado.certificados.length} certificados`);
          resultado.certificados.forEach(cert => {
            this.adicionarLog(`  - ${cert.subjectName}`);
          });
        });

        // Combinar todos os certificados encontrados
        let todosCertificados = [];
        resultados.forEach(resultado => {
          todosCertificados = todosCertificados.concat(resultado.certificados);
        });

        this.certificados = todosCertificados.map(cert => {
          return {
            ...cert,
            cpf: this.extrairDocumento(cert.subjectName),
            nome: this.extrairNome(cert.subjectName),
            email: this.extrairEmail(cert.subjectName),
            isValid: new Date(cert.validTo || cert.validityEnd) > new Date()
          };
        });

        this.adicionarLog(`✅ Total de certificados carregados: ${this.certificados.length}`);
        
        if (this.certificados.length === 0) {
          this.$bvToast.toast('Nenhum certificado encontrado no debug', {
            title: 'Debug',
            variant: 'warning'
          });
        }

      } catch (error) {
        this.adicionarLog(`❌ Erro no debug: ${error.message}`);
        this.$bvToast.toast(`Erro no debug: ${error.message}`, {
          title: 'Erro',
          variant: 'danger'
        });
      } finally {
        this.loading = false;
        this.currentAction = '';
      }
    },

    selecionarCertificado(certificado) {
      this.certificadoSelecionado = certificado;
      this.adicionarLog(`Certificado selecionado: ${this.extrairNome(certificado.subjectName)}`);
      this.assinatura = ''; // Limpa assinatura anterior
      this.loginResult = null; // Limpa resultado anterior
    },

    async testarLoginCertificado() {
      if (!this.certificadoSelecionado) {
        this.$bvToast.toast('Selecione um certificado', {
          title: 'Aviso',
          variant: 'warning'
        });
        return;
      }

      this.loading = true;
      this.currentAction = 'login';
      this.loginResult = null;
      this.adicionarLog('Iniciando teste de login com certificado...');
      this.adicionarLog(`Conectando com PKI backend: ${process.env.VUE_APP_baseURLAss}pki/login/cenprotPublico`);

      try {
        // Usar a mesma estrutura do sistema existente
        const res = await APIConsulta.get(`pki/login/cenprotPublico`);
        
        this.adicionarLog('Resposta recebida do servidor PKI');
        
        if (res && res.data && res.data.ic_autenticado > 0) {
          // Armazenar dados do certificado
          const certData = res.data;
          
          this.adicionarLog(`Certificado autenticado - Tipo: ${certData.sg_tipo_documento}, Documento: ${certData.nm_nr_documento}`);
          
          localStorage.setItem("nm_thumbprint", certData.nm_thumbprint);
          localStorage.setItem("nm_thumbprint_sha256", certData.nm_thumbprint_sha256);
          localStorage.setItem("sg_tipo_documento", certData.sg_tipo_documento);
          localStorage.setItem("nm_nr_documento_cert", certData.nm_nr_documento);
          localStorage.setItem("SSL_CLIENT_S_DN_CN", certData.SSL_CLIENT_S_DN_CN);
          localStorage.setItem("nm_tipo_certificado", certData.nm_tipo_certificado);
          localStorage.setItem("SSL_CLIENT_S_DN", certData.SSL_CLIENT_S_DN);
          localStorage.setItem("SSL_CLIENT_V_START", certData.SSL_CLIENT_V_START);
          localStorage.setItem("SSL_CLIENT_V_END", certData.SSL_CLIENT_V_END);

          if (certData.sg_tipo_documento == "CNPJ") {
            localStorage.setItem("libProcuracao", 'true');
          } else {
            localStorage.setItem("libProcuracao", 'false');
          }
          localStorage.setItem("libAnuencia", 'true');

          // Criptografar documento para envio
          let password = process.env.VUE_APP_CHAVE_CRIPTOGRAFIA.toString();
          let encrypted = CryptoJSAesJson.encrypt(certData.nm_nr_documento, password);
          let encryptedUrl = btoa(encrypted);
          
          // Fazer login no backend
          const resLogin = await API.post(`auth/login/cert`, { cpf: encryptedUrl });
          
          if (resLogin && resLogin.data) {
            // Armazenar tokens de sessão
            localStorage.setItem("jwtToken", resLogin.data.token);
            sessionStorage.setItem("jwtToken", resLogin.data.token);
            localStorage.setItem("usrName", resLogin.data.name);
            localStorage.setItem("usrEmail", resLogin.data.email);
            localStorage.setItem("usrCpf", resLogin.data.cpf);
            
            this.loginResult = {
              success: true,
              message: 'Login com certificado digital realizado com sucesso!',
              userInfo: {
                name: resLogin.data.name,
                email: resLogin.data.email,
                cpf: resLogin.data.cpf
              }
            };
            
            this.adicionarLog('Login certificado realizado com sucesso!');
            this.logged = true;
            
            this.$bvToast.toast('Login realizado com sucesso!', {
              title: 'Sucesso',
              variant: 'success'
            });
            
          } else {
            throw new Error('Resposta inválida do servidor de autenticação');
          }
          
        } else {
          throw new Error('Certificado não foi autenticado pelo servidor PKI');
        }
        
      } catch (error) {
        let errorMessage = error.message || 'Erro desconhecido no login';
        
        // Verificar se é erro de conexão
        if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
          errorMessage = 'Não foi possível conectar com o servidor PKI. Verifique sua conexão.';
        } else if (error.response) {
          const serverMessage = error.response.data && error.response.data.message ? error.response.data.message : 'Erro no servidor';
          errorMessage = `Erro do servidor PKI (${error.response.status}): ${serverMessage}`;
        }
        
        this.loginResult = {
          success: false,
          message: errorMessage
        };
        
        this.adicionarLog(`Erro no login PKI: ${errorMessage}`);
        this.adicionarLog(`URL PKI utilizada: ${process.env.VUE_APP_baseURLAss}pki/login/cenprotPublico`);
        
        this.$bvToast.toast(`Erro no login: ${errorMessage}`, {
          title: 'Erro PKI',
          variant: 'danger'
        });
      } finally {
        this.loading = false;
        this.currentAction = '';
      }
    },

    async assinarTexto() {
      if (!this.certificadoSelecionado || !this.textoParaAssinar.trim()) {
        this.$bvToast.toast('Selecione um certificado e digite um texto', {
          title: 'Aviso',
          variant: 'warning'
        });
        return;
      }

      this.loading = true;
      this.currentAction = 'assinar';
      this.adicionarLog('Iniciando processo de assinatura...');

      try {
        // Converter texto para bytes
        const bytesToSign = this.stringToBytes(this.textoParaAssinar);
        
        // Realizar assinatura
        const signature = await this.pki.signData({
          thumbprint: this.certificadoSelecionado.thumbprint,
          data: bytesToSign,
          digestAlgorithm: 'SHA-256'
        });

        this.assinatura = signature;
        this.adicionarLog('Texto assinado com sucesso');
        
        this.$bvToast.toast('Texto assinado com sucesso!', {
          title: 'Sucesso',
          variant: 'success'
        });

      } catch (error) {
        this.adicionarLog(`Erro ao assinar: ${error.message}`);
        this.$bvToast.toast(`Erro ao assinar: ${error.message}`, {
          title: 'Erro',
          variant: 'danger'
        });
      } finally {
        this.loading = false;
        this.currentAction = '';
      }
    },

    copiarAssinatura() {
      navigator.clipboard.writeText(this.assinatura).then(() => {
        this.$bvToast.toast('Assinatura copiada para a área de transferência', {
          title: 'Sucesso',
          variant: 'success'
        });
        this.adicionarLog('Assinatura copiada para clipboard');
      }).catch(() => {
        this.$bvToast.toast('Erro ao copiar assinatura', {
          title: 'Erro',
          variant: 'danger'
        });
      });
    },

    mostrarInstrucoesInstalacao() {
      this.$bvModal.msgBoxOk(
        `Para usar a assinatura digital, você precisa instalar o plugin Web PKI da Lacuna Software.
        
        1. Acesse: https://www.lacunasoftware.com/pt/home/webpki
        2. Baixe e instale o plugin para seu navegador
        3. Reinicie o navegador
        4. Volte a esta página e clique em "Verificar Plugin"`,
        {
          title: 'Plugin Web PKI não instalado',
          size: 'lg',
          buttonSize: 'sm',
          okVariant: 'primary',
          headerClass: 'p-2 border-bottom-0',
          footerClass: 'p-2 border-top-0'
        }
      );
    },

    async testarLoginComRestPKI() {
      if (!this.certificadoSelecionado) {
        this.$bvToast.toast('Selecione um certificado', {
          title: 'Aviso',
          variant: 'warning'
        });
        return;
      }

      this.loading = true;
      this.currentAction = 'login';
      this.loginResult = null;
      this.adicionarLog('🚀 Iniciando login com Rest PKI Core...');

      try {
        // Etapa 1: Validar certificado no Rest PKI Core
        this.adicionarLog('📋 Etapa 1: Validando certificado no Rest PKI Core...');
        const validacaoResult = await RestPKICore.validarCertificado(this.certificadoSelecionado.encoding);
        
        if (!validacaoResult.isValid) {
          throw new Error('Certificado inválido segundo Rest PKI Core');
        }
        
        this.adicionarLog(`✅ Certificado válido! CPF/CNPJ: ${validacaoResult.userInfo.cpf || validacaoResult.userInfo.cnpj}`);
        
        // Etapa 2: Chamar seu backend com dados validados
        this.adicionarLog('🔐 Etapa 2: Autenticando no backend principal...');
        
        const loginData = {
          // Dados extraídos e validados pelo Rest PKI Core
          cpf_cnpj: validacaoResult.userInfo.cpf || validacaoResult.userInfo.cnpj,
          tipo_documento: validacaoResult.userInfo.tipoPessoa, // 'CPF' ou 'CNPJ'
          nome_certificado: validacaoResult.userInfo.nome,
          
          // Dados do certificado para auditoria
          certificado_thumbprint: validacaoResult.certificate.thumbprint,
          certificado_serial: validacaoResult.certificate.serialNumber,
          certificado_validade: validacaoResult.certificate.validityEnd,
          
          // Flag indicando que foi validado via Rest PKI Core
          origem_validacao: 'rest-pki-core'
        };
        
        // Chamar endpoint específico para login validado via Rest PKI Core
        const backendResponse = await API.post('auth/login-certificado-restpki', loginData);
        
        if (backendResponse.data && backendResponse.data.success) {
          // Etapa 3: Armazenar dados de sessão
          this.adicionarLog('🎉 Etapa 3: Login realizado com sucesso!');
          
          const userData = backendResponse.data.user;
          const token = backendResponse.data.token;
          
          // Armazenar token JWT
          localStorage.setItem("jwtToken", token);
          sessionStorage.setItem("jwtToken", token);
          
          // Armazenar dados do usuário
          localStorage.setItem("usrName", userData.name);
          localStorage.setItem("usrEmail", userData.email);
          localStorage.setItem("usrCpf", userData.cpf || userData.cnpj);
          
          // Armazenar dados do certificado
          localStorage.setItem("nm_thumbprint", validacaoResult.certificate.thumbprint);
          localStorage.setItem("sg_tipo_documento", validacaoResult.userInfo.tipoPessoa);
          localStorage.setItem("certificado_origem", "rest-pki-core");
          
          // Definir permissões
          if (validacaoResult.userInfo.tipoPessoa === "CNPJ") {
            localStorage.setItem("libProcuracao", 'true');
          } else {
            localStorage.setItem("libProcuracao", 'false');
          }
          localStorage.setItem("libAnuencia", 'true');
          
          this.loginResult = {
            success: true,
            message: 'Login realizado com sucesso via Rest PKI Core!',
            userInfo: userData,
            certificadoInfo: validacaoResult.userInfo
          };
          
          this.logged = true;
          
          this.$bvToast.toast('Login com certificado realizado!', {
            title: 'Sucesso - Rest PKI Core',
            variant: 'success'
          });
          
        } else {
          throw new Error(backendResponse.data.message || 'Erro na autenticação do backend');
        }
        
      } catch (error) {
        let errorMessage = error.message || 'Erro desconhecido no login';
        
        if (error.response) {
          if (error.response.status === 404) {
            errorMessage = 'Usuário não encontrado na base de dados';
          } else if (error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          }
        }
        
        this.loginResult = {
          success: false,
          message: errorMessage
        };
        
        this.adicionarLog(`❌ Erro: ${errorMessage}`);
        
        this.$bvToast.toast(`Erro: ${errorMessage}`, {
          title: 'Erro no Login',
          variant: 'danger'
        });
      } finally {
        this.loading = false;
        this.currentAction = '';
      }
    },

  async fazerLoginRestPKI() {
      if (!this.certificadoSelecionado) {
          this.$bvToast.toast('Selecione um certificado', { title: 'Aviso', variant: 'warning' });
          return;
      }

      this.loading = true;
      this.currentAction = 'login-restpki';
      this.adicionarLog('🚀 Iniciando login seguro via Backend...');

      try {
          // --- PASSO 1: Start ---
          this.adicionarLog('1️⃣ Solicitando desafio (nonce) ao servidor...');
          const startResponse = await API.post('auth/cert/start');
          
          const { token, nonce, digestAlgorithm } = startResponse.data;
          this.adicionarLog(`✅ Desafio recebido. Token Sessão: ${token}`);

          // --- PASSO 2: Assinatura (Client-side) ---
          this.adicionarLog('2️⃣ Assinando desafio com o certificado...');

          const signature = await new Promise((resolve, reject) => {
              this.pki.signHash({
                  thumbprint: this.certificadoSelecionado.thumbprint,
                  hash: nonce,
                  digestAlgorithm: digestAlgorithm
              })
              .success((signatureString) => resolve(signatureString))
              .fail((error) => reject(new Error("Erro ao assinar: " + error.message)));
          });

          this.adicionarLog('✅ Assinatura gerada localmente.');

          const certificadoBase64 = await new Promise((resolve, reject) => {
              this.pki.readCertificate(this.certificadoSelecionado.thumbprint)
                  .success((content) => resolve(content))
                  .fail((ex) => reject(new Error("Erro ao ler certificado: " + ex.message)));
          });

          // --- PASSO 4: Complete (Envio ao Backend) ---
          this.adicionarLog('3️⃣ Enviando assinatura para validação...');

          const completeResponse = await API.post('auth/cert/complete', {
              token: token,
              signature: signature,
              certificate: certificadoBase64
          });

          // Se chegou aqui, o status é 2xx (Sucesso).
          // Ajustado para ler a estrutura plana do seu Backend PHP
          const responseData = completeResponse.data;

          // Salvar Sessão (Limpamos as duplicidades)
          localStorage.setItem("jwtToken", responseData.token);
          localStorage.setItem("usrName", responseData.name);
          localStorage.setItem("usrEmail", responseData.email);
          localStorage.setItem("usrCpf", responseData.cpf);
          
          // Se houver lógica de termo de uso
          if (responseData.ic_atualizacao_termo) {
              localStorage.setItem("tokenTermo", responseData.ic_atualizacao_termo);
          }

          this.loginResult = {
              success: true,
              message: 'Login realizado com sucesso!',
              userInfo: {
                  name: responseData.name,
                  email: responseData.email,
                  cpf: responseData.cpf
              }
          };

          this.logged = true;
          this.$bvToast.toast('Login realizado!', { variant: 'success' });
          this.adicionarLog('🎉 Login concluído com sucesso!');

          // Opcional: Redirecionar usuário
          // this.$router.push('/dashboard'); 

      } catch (error) {
          let msg = 'Erro desconhecido';
          
          // Tratamento para pegar a mensagem exata do Laravel return response()->json(['message' => ...], 400)
          if (error.response && error.response.data && error.response.data.message) {
              msg = error.response.data.message;
          } else if (error.message) {
              msg = error.message;
          }

          this.loginResult = { success: false, message: msg };
          this.adicionarLog(`❌ Falha: ${msg}`);
          this.$bvToast.toast(msg, { title: 'Erro no Login', variant: 'danger' });
          
      } finally {
          this.loading = false;
          this.currentAction = '';
      }
  },

  // Função auxiliar necessária para converter o Nonce do Laravel
  base64ToArray(base64) {
    const binary_string = window.atob(base64);
    const len = binary_string.length;
    const bytes = []; // Cria um Array simples (Lista), não um TypedArray
    for (let i = 0; i < len; i++) {
      bytes.push(binary_string.charCodeAt(i));
    }
    return bytes;
  },

    // Funções auxiliares
    extrairNome(subjectName) {
      const match = subjectName.match(/CN=([^,]+)/);
      return match ? match[1] : subjectName;
    },

    extrairDocumento(subjectName) {
      // Tenta extrair CPF ou CNPJ do subject name
      const cpfMatch = subjectName.match(/(\d{3}\.?\d{3}\.?\d{3}-?\d{2})/);
      if (cpfMatch) {
        return cpfMatch[1];
      }
      
      const cnpjMatch = subjectName.match(/(\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2})/);
      if (cnpjMatch) {
        return cnpjMatch[1];
      }
      
      return 'Não identificado';
    },

    formatarData(dataISO) {
      return new Date(dataISO).toLocaleDateString('pt-BR');
    },

    stringToBytes(str) {
      return new TextEncoder().encode(str);
    },

    adicionarLog(message) {
      const timestamp = new Date().toLocaleTimeString();
      this.logs.unshift({ timestamp, message });
      
      // Manter apenas os últimos 50 logs
      if (this.logs.length > 50) {
        this.logs = this.logs.slice(0, 50);
      }
    }
  }
}
</script>

<style scoped>
.container {
  margin-top: 20px;
  margin-bottom: 40px;
}

.card {
  border: none;
  border-radius: 15px;
}

.card-header {
  border-radius: 15px 15px 0 0 !important;
  background: linear-gradient(135deg, #007bff, #0056b3);
}

.btn {
  border-radius: 8px;
  font-weight: 500;
}

.list-group-item {
  border-radius: 8px !important;
  margin-bottom: 5px;
  border: 1px solid #dee2e6;
}

.list-group-item.active {
  background-color: #007bff;
  border-color: #007bff;
}

.alert {
  border-radius: 8px;
}

textarea {
  border-radius: 8px;
}

.border {
  border-radius: 8px !important;
}

/* Animação para botões carregando */
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Scrollbar personalizada para logs */
.border::-webkit-scrollbar {
  width: 8px;
}

.border::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.border::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.border::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
